import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useFeathers } from '../../app/util';
import { DataDetailToolbar, LoadingSpinner, Tile } from '../../components';
import { HybridMapTreeIcon } from '../../components/HybridMapTreeIcon';
import { Settings } from '@mui/icons-material';
import { DistrictIssues } from '../../components/DistrictIssues';
import { IconButton, Tooltip } from '@mui/material';
import { District } from '../../app/feathers/districts/District';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { humanizeDistrictType } from "../../app/util/humanize-district-type";


const hasIncompleteChildrenGeodistrictByType = (district: District | null, districtType: string) => (
  district?.childrenHaveGeodistrictByType?.[districtType] === false
);

const StateDetail = () => {
  const history = useHistory();
  const matchName = useParams<{ matchName: string }>().matchName;
  const stateCode = matchName.split('-')[0];
  const [loading, setLoading] = useState(true);
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [district, setDistrict] = useState<District | null>(null);
  const [districtCountsByType, setDistrictCountsByType] = useState({} as Record<string, number>);
  const [districtTypes, setDistrictTypes] = useState<string[]>([]);
  const [tier2Issues, setTier2Issues] = useState<Record<string, string[]>>({});
  const [humanizedDistrictTypeNames, setHumanizedDistrictTypeNames] = useState<Record<string, string>>({})

  async function loadState() {
    setLoading(true);
    const districtService = feathers.getService('districts');
    const stateConfigService = feathers.getService('state-configurations');
    const [
      districts,
      stateConfig,
    ] = await Promise.all([
      districtService.find({ query: { matchName } }),
      stateConfigService.get(stateCode),
    ]);
    const district = districts.data[0];
    const configuredTypes = stateConfig.typeStructure?.state || [];

    if (!district) {
      setLoading(false);
      return;
    }

    setDistrict(district);
    const districtTypes = [...configuredTypes];
    const humanizedDistrictTypeNames = districtTypes.reduce(
      (humanizedTypeNames, type) => {
        humanizedTypeNames[type] = humanizeDistrictType(type, stateConfig.typeConfigurations);
        return humanizedTypeNames;
      },
      {} as Record<string, string>,
    );
    setHumanizedDistrictTypeNames(humanizedDistrictTypeNames);
    districtTypes.sort((a, b) => (a && humanizedDistrictTypeNames[a]).localeCompare(b && humanizedDistrictTypeNames[b]));
    setDistrictTypes(districtTypes);

    const tier2Issues: Record<string, string[]> = {};
    districtTypes.forEach(districtType => {
      tier2Issues[districtType] = [];
      if (hasIncompleteChildrenGeodistrictByType(district, districtType)) {
        tier2Issues[districtType].push('children-coverage-incomplete');
      }
    });

    setTier2Issues(tier2Issues);
    const countsByTypeEntries = await Promise.all(
      districtTypes.map(async (districtType) => {
        const { total } = await districtService.find({
          query: {
            parentId: district._id,
            type: districtType,
            $limit: 0,
          },
        });
        return [districtType, total || 0] as const;
      }),
    );
    setDistrictCountsByType(Object.fromEntries(countsByTypeEntries));
    setLoading(false);
  }

  useEffect(() => {
    if (!feathers) {
      return;
    }
    loadState();
  }, [feathers]);

  const stateIssueTypes = [
    {
      Icon: HybridMapTreeIcon,
      key: 'children-coverage-incomplete',
      tooltips: [
        'These districts have geoboundaries for all child districts.',
        'These districts are missing geoboundaries for one or more child district types!',
      ],
    },
  ];

  return (
    <Wrapper>
      <DataDetailToolbar
        navTree={[
          { text: 'Districts', to: '/states' },
          { text: district?.name || '--' },
        ]}
        onBack={() => history.push('/states')}
        actionButtonsComponent={
          <Link
            to={`/states/${matchName}/state-config`}
          >
            <Tooltip title="Configure State">
              <IconButton color="primary">
                <Settings/>
              </IconButton>
            </Tooltip>
          </Link>
        }
      />
      <div style={{ height: '12px' }}/>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
      >
        <h2>{district?.name} Districts</h2>
      </div>
      {
        loading
          ?
          <div style={{ display: 'flex', justifyContent: 'space-around' }}>
            <LoadingSpinner />
          </div>
          :
          districtTypes.length
            ?
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              {
                districtTypes.map((districtType) => {
                  return (
                    <Tile
                      key={districtType}
                      shadowEnabled={true}
                      onClick={{ to: `/states/${matchName}/${districtType}` }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 8,
                        }}>
                          <h3 style={{
                            textTransform: 'capitalize',
                          }}>
                            {humanizedDistrictTypeNames[districtType]}
                          </h3>
                          <small>
                            ({districtCountsByType[districtType] || 0})
                          </small>
                        </div>
                        <DistrictIssues
                          style={{
                            marginBottom: 8,
                            marginRight: 24,
                          }}
                          issues={tier2Issues[districtType]}
                          issueTypes={stateIssueTypes}
                        />
                      </div>
                    </Tile>
                  );
                })
              }
            </div>
            :
            <div>
              No {district?.name} Districts added.
            </div>
      }
    </Wrapper>
  );
};


const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

export default StateDetail;
